/**
 * Spec 004 US3 (T019) — Per-layer OCR pipeline progress component.
 *
 * Renders three rows (eRechnung-XML / OCR Tesseract / KI-Fallback)
 * with state markers (idle / running / success / fail) and a
 * liveness indicator on the currently-running layer per
 * FR-G3-OCR-LIVENESS.
 *
 * Animation discipline (FR-UI-005 + Constitution V):
 *   - Pulsing dot via Framer Motion when `status === 'running'` AND
 *     `useAnimationEnabled()` returns true.
 *   - When animation is gated off (prefers-reduced-motion, master
 *     toggle, or off-list context), shows static "läuft…" text +
 *     flat colour dot. State changes still announce via aria-live
 *     polite, so screen-reader users get progress feedback in both
 *     modes.
 *
 * The pure derivation lives in `ocrPipelineLayers.ts`; this file is
 * presentational.
 */
import { motion } from 'framer-motion';
import {
  deriveOcrLayers,
  OCR_LAYER_LABEL,
  type OcrLayer,
  type OcrLayerStatus,
  type OcrLayersInput,
} from './ocrPipelineLayers';
import { useAnimationEnabled } from '../../hooks/useAnimationEnabled';

interface Props {
  /** Inputs passed straight through to `deriveOcrLayers()`. */
  layersInput: OcrLayersInput;
  className?: string;
}

const STATUS_DOT: Record<OcrLayerStatus, string> = {
  idle: 'bg-white/20',
  running: 'bg-amber-400',
  success: 'bg-emerald-400',
  fail: 'bg-red-400',
};

const STATUS_LABEL: Record<OcrLayerStatus, string> = {
  idle: 'wartet',
  running: 'läuft…',
  success: '✓ fertig',
  fail: '✗ fehlgeschlagen',
};

function LiveDot({ animated, status }: { animated: boolean; status: OcrLayerStatus }) {
  // Running + animation enabled → Framer pulse. Anything else → flat.
  if (animated && status === 'running') {
    return (
      <motion.span
        className={`inline-block w-2.5 h-2.5 rounded-full ${STATUS_DOT.running}`}
        animate={{ opacity: [0.4, 1, 0.4], scale: [0.85, 1.0, 0.85] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
        aria-hidden="true"
      />
    );
  }
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${STATUS_DOT[status]}`}
      aria-hidden="true"
    />
  );
}

export function OcrPipelineProgress({ layersInput, className = '' }: Props) {
  const layers = deriveOcrLayers(layersInput);
  const animated = useAnimationEnabled();

  // Build one consolidated aria-live announcement for the most-
  // recently-changed layer state. Tested separately in T021 via the
  // reduce-motion verification path.
  const liveStates = layers
    .map((l) => `${OCR_LAYER_LABEL[l.layer]}: ${STATUS_LABEL[l.status]}`)
    .join(' · ');

  return (
    <div
      data-testid="ocr-pipeline-progress"
      className={`rounded-lg border border-white/10 bg-white/[0.02] p-3 ${className}`}
    >
      <h3 className="text-xs uppercase tracking-wider text-white/50 mb-2">
        OCR-Pipeline
      </h3>
      <span className="sr-only" role="status" aria-live="polite">
        {liveStates}
      </span>
      <ul className="space-y-1.5">
        {layers.map((l: { layer: OcrLayer; status: OcrLayerStatus; failReason?: string }) => (
          <li
            key={l.layer}
            data-testid={`ocr-layer-${l.layer}-${l.status}`}
            className="flex items-center gap-2 text-sm text-white/80"
          >
            <LiveDot animated={animated} status={l.status} />
            <span className="flex-1 min-w-0">
              <span className="font-medium">{OCR_LAYER_LABEL[l.layer]}</span>
              <span className="ml-2 text-xs text-white/50">
                {STATUS_LABEL[l.status]}
              </span>
            </span>
            {l.status === 'fail' && l.failReason && (
              <span
                className="text-xs text-red-300/80 truncate max-w-[40ch]"
                title={l.failReason}
              >
                {l.failReason}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
